import type { RichText } from '@pbk-web/types';
import { RichTextType } from '@pbk-web/types';
import { marked } from 'marked';

/**
 * HTML tags → permitted attributes for rich-text form content.
 * - Also covers everything the Lexical editor exports (heading alignment, sub/superscript, check lists,
 *   column widths, striped tables, list numbering, image sizes, dynamic-data chips), so pasting its own HTML loses nothing.
 */
export const fullAllowedHtmlTags = {
  p: ['style'],
  h1: ['style'],
  h2: ['style'],
  h3: ['style'],
  h4: ['style'],
  h5: ['style'],
  h6: ['style'],
  strong: [],
  em: [],
  u: [],
  s: [],
  sub: [],
  sup: [],
  a: ['href', 'data-placeholder-link', 'target', 'rel'],
  img: ['src', 'alt', 'class', 'data-file-id', 'width', 'height'],
  ul: ['style', '__lexicallisttype'],
  ol: ['style', 'data-lexical-numbering'],
  li: ['role', 'aria-checked'],
  blockquote: ['style'],
  code: [],
  pre: [],
  br: [],
  hr: [],
  table: ['style', 'data-lexical-row-striping'],
  colgroup: [],
  col: ['style'],
  thead: [],
  tbody: [],
  tfoot: [],
  tr: [],
  th: ['style', 'colspan', 'rowspan', 'class'],
  td: ['style', 'colspan', 'rowspan', 'class'],
  span: ['class', 'contentEditable', 'contenteditable', 'data-lexical-dynamic-data'],
};

/** Normalize a string (markdown) or an existing `RichText` into `{ type, value }` HTML. */
export const convertToRichText = (input: string | RichText): RichText | null => {
  if (typeof input === 'string') {
    input = input.replace(/\\n/g, '<br />');
    const html = marked(input, { async: false, breaks: true }) as string;
    return {
      type: RichTextType.HTML,
      value: html,
    };
  } else if (
    typeof input === 'object' &&
    input !== null &&
    'type' in input &&
    typeof input.type === 'number' &&
    'value' in input &&
    typeof input.value === 'string' &&
    Object.keys(input).length === 2
  ) {
    return input as RichText;
  }

  return null;
};

/** Repair colspans that exceed a table's column count (a paste artifact). */
export function removeInvalidColspans(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('tr').forEach((row) => {
    let totalColumns = 0;

    // Calculate the total columns in the row
    Array.from(row.children).forEach((cell) => {
      const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);

      // If colspan is invalid or less than 1, remove it
      if (isNaN(colspan) || colspan < 1) {
        cell.removeAttribute('colspan');
      } else {
        totalColumns += colspan;
      }
    });

    // Check for mismatched columns in the table and fix it if needed
    const table = row.closest('table');
    if (table) {
      const expectedColumns = Array.from(table.querySelectorAll('tr')).reduce((max, tableRow) => Math.max(max, tableRow.children.length), 0);

      // If the totalColumns exceeds expectedColumns, remove or adjust colspans
      if (totalColumns > expectedColumns) {
        Array.from(row.children).forEach((cell) => {
          const colspan = parseInt(cell.getAttribute('colspan') || '1', 10);
          if (colspan && totalColumns > expectedColumns) {
            const adjustedColspan = Math.max(1, colspan - (totalColumns - expectedColumns));
            cell.setAttribute('colspan', adjustedColspan.toString());
            totalColumns -= colspan - adjustedColspan;
          }
        });
      }
    }
  });

  return doc.body.innerHTML;
}
